"use client";

import { useState, useMemo } from "react";
import clsx from "clsx";
import { useTradeStore } from "@/stores/tradeStore";

type OrderSide = "long" | "short";
type OrderType = "market" | "limit";

interface OrderFormData {
  side: OrderSide;
  type: OrderType;
  size: string;
  price: string;
  leverage: number;
  reduceOnly: boolean;
}

const LEVERAGE_OPTIONS = [1, 2, 5, 10, 20];
const MIN_ORDER_SIZE = 10;
const MAX_ORDER_SIZE = 100000;
const TAKER_FEE_RATE = 0.0005;
const MAKER_FEE_RATE = 0.0002;
const MAINTENANCE_MARGIN_RATE = 0.005;

interface OrderEntryPanelProps {
  currentPrice: number;
  accountBalance: number;
  marginUsed: number;
  onSubmitOrder?: (order: OrderFormData) => void;
  disabled?: boolean;
}

export const OrderEntryPanel = ({
  currentPrice,
  accountBalance,
  marginUsed,
  onSubmitOrder,
  disabled = false,
}: OrderEntryPanelProps) => {
  const { market } = useTradeStore();
  const [formData, setFormData] = useState<OrderFormData>({
    side: "long",
    type: "market",
    size: "",
    price: "",
    leverage: 5,
    reduceOnly: false,
  });

  const availableBalance = accountBalance - marginUsed;

  const calculations = useMemo(() => {
    const size = parseFloat(formData.size) || 0;
    const price =
      formData.type === "market"
        ? currentPrice
        : parseFloat(formData.price) || currentPrice;
    const leverage = formData.leverage;

    const notionalValue = size;
    const requiredMargin = notionalValue / leverage;
    const feeRate = formData.type === "market" ? TAKER_FEE_RATE : MAKER_FEE_RATE;
    const estimatedFee = notionalValue * feeRate;

    // Liquidation price calculation (isolated margin)
    let liquidationPrice = 0;
    if (formData.side === "long") {
      liquidationPrice = price * (1 - 1 / leverage + MAINTENANCE_MARGIN_RATE);
    } else {
      liquidationPrice = price * (1 + 1 / leverage - MAINTENANCE_MARGIN_RATE);
    }

    const maxSize = availableBalance * leverage;

    return {
      notionalValue,
      requiredMargin,
      estimatedFee,
      liquidationPrice,
      maxSize,
      isValid:
        size >= MIN_ORDER_SIZE &&
        size <= MAX_ORDER_SIZE &&
        requiredMargin <= availableBalance,
    };
  }, [formData, currentPrice, availableBalance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!calculations.isValid || disabled) return;
    onSubmitOrder?.(formData);
  };

  const handleSizePercentage = (percentage: number) => {
    const maxSize = calculations.maxSize;
    const newSize = ((maxSize * percentage) / 100).toFixed(2);
    setFormData((prev) => ({ ...prev, size: newSize }));
  };

  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/5 p-4 backdrop-blur">
      <h3 className="mb-4 text-sm font-semibold text-white">Order Entry</h3>

      {/* Side Toggle */}
      <div className="mb-4 flex rounded-lg bg-slate-800 p-1">
        <button
          type="button"
          onClick={() => setFormData((prev) => ({ ...prev, side: "long" }))}
          className={clsx(
            "flex-1 rounded-md py-2 text-sm font-semibold transition",
            formData.side === "long"
              ? "bg-emerald-500 text-white"
              : "text-slate-400 hover:text-white"
          )}
        >
          Long
        </button>
        <button
          type="button"
          onClick={() => setFormData((prev) => ({ ...prev, side: "short" }))}
          className={clsx(
            "flex-1 rounded-md py-2 text-sm font-semibold transition",
            formData.side === "short"
              ? "bg-rose-500 text-white"
              : "text-slate-400 hover:text-white"
          )}
        >
          Short
        </button>
      </div>

      {/* Order Type Toggle */}
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setFormData((prev) => ({ ...prev, type: "market" }))}
          className={clsx(
            "flex-1 rounded-lg border py-2 text-xs font-medium transition",
            formData.type === "market"
              ? "border-blue-500 bg-blue-500/20 text-blue-400"
              : "border-slate-600 text-slate-400 hover:border-slate-500"
          )}
        >
          Market
        </button>
        <button
          type="button"
          onClick={() => setFormData((prev) => ({ ...prev, type: "limit" }))}
          className={clsx(
            "flex-1 rounded-lg border py-2 text-xs font-medium transition",
            formData.type === "limit"
              ? "border-blue-500 bg-blue-500/20 text-blue-400"
              : "border-slate-600 text-slate-400 hover:border-slate-500"
          )}
        >
          Limit
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Price Input (Limit orders only) */}
        {formData.type === "limit" && (
          <div>
            <label className="mb-1 block text-xs text-slate-400">Price (USD)</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, price: e.target.value }))
              }
              placeholder={currentPrice.toFixed(2)}
              className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              step="0.01"
              min="0"
            />
          </div>
        )}

        {/* Size Input */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs text-slate-400">Size (USD)</label>
            <span className="text-xs text-slate-500">
              Max: $
              {calculations.maxSize.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <input
            type="number"
            value={formData.size}
            onChange={(e) => setFormData((prev) => ({ ...prev, size: e.target.value }))}
            placeholder="0.00"
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            step="0.01"
            min={MIN_ORDER_SIZE}
            max={MAX_ORDER_SIZE}
          />
          <div className="mt-2 flex gap-2">
            {[25, 50, 75, 100].map((pct) => (
              <button
                key={pct}
                type="button"
                onClick={() => handleSizePercentage(pct)}
                className="flex-1 rounded border border-slate-600 py-1 text-xs text-slate-400 transition hover:border-slate-500 hover:text-white"
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>

        {/* Leverage Selector */}
        <div>
          <label className="mb-1 block text-xs text-slate-400">Leverage</label>
          <div className="flex gap-1">
            {LEVERAGE_OPTIONS.map((lev) => (
              <button
                key={lev}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, leverage: lev }))}
                className={clsx(
                  "flex-1 rounded-lg py-2 text-xs font-semibold transition",
                  formData.leverage === lev
                    ? "bg-blue-500 text-white"
                    : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                )}
              >
                {lev}x
              </button>
            ))}
          </div>
        </div>

        {/* Reduce Only Toggle */}
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={formData.reduceOnly}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, reduceOnly: e.target.checked }))
            }
            className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
          />
          Reduce Only
        </label>

        {/* Order Summary */}
        <div className="rounded-lg bg-slate-800/50 p-3 text-xs">
          <div className="flex justify-between text-slate-400">
            <span>Required Margin</span>
            <span className="text-white">${calculations.requiredMargin.toFixed(2)}</span>
          </div>
          <div className="mt-1 flex justify-between text-slate-400">
            <span>Est. Fee ({formData.type === "market" ? "0.05%" : "0.02%"})</span>
            <span className="text-white">${calculations.estimatedFee.toFixed(2)}</span>
          </div>
          <div className="mt-1 flex justify-between text-slate-400">
            <span>Liquidation Price</span>
            <span
              className={clsx(
                "font-medium",
                formData.side === "long" ? "text-rose-400" : "text-emerald-400"
              )}
            >
              ${calculations.liquidationPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!calculations.isValid || disabled}
          className={clsx(
            "w-full rounded-lg py-3 text-sm font-semibold transition",
            formData.side === "long"
              ? "bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50"
              : "bg-rose-500 hover:bg-rose-600 disabled:bg-rose-500/50",
            "text-white disabled:cursor-not-allowed"
          )}
        >
          {formData.side === "long" ? "Open Long" : "Open Short"} {market}
        </button>

        {/* Available Balance */}
        <div className="text-center text-xs text-slate-500">
          Available: $
          {availableBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
      </form>
    </div>
  );
};
